import React from "react";
import {
  ArrowLeft,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Phone,
  Mail,
  MapPin,
} from "lucide-react";
import HeaderComponent from "./Home/HeaderComponent";
import Footer from "./Home/Footer";
import { useNavigate } from "react-router-dom";

const RefundPolicy = () => {
  const lastUpdated = "January 15, 2025";
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
      <HeaderComponent />
      <section className="py-12 px-4 bg-gradient-to-r from-orange-500 to-red-500 text-white">
        <div className="container mx-auto max-w-4xl">
          <div className="flex items-center mb-6">
            <button
              onClick={() => navigate("/")}
              className="flex items-center text-white hover:text-orange-100 transition-all mr-6"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </button>
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-2xl">
              💰
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-4">Refund Policy</h1>
          <p className="text-xl">
            Simple and transparent refund terms for your peace of mind
          </p>
          <p className="text-sm mt-4 opacity-90">Last updated: {lastUpdated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            {/* Introduction */}
            <div className="mb-12 p-6 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start">
                <DollarSign className="h-6 w-6 text-green-500 mr-3 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Our Simple Refund Promise
                  </h3>
                  <p className="text-gray-600">
                    At MahalaxmiPuja, we believe in complete transparency. Our
                    refund policy is straightforward:
                    <strong>
                      {" "}
                      full refund before puja date, no refund after puja
                      completion.
                    </strong>
                    All approved refunds are processed within 3 working days
                    with no hidden conditions.
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Summary */}
            <div className="mb-12 grid md:grid-cols-2 gap-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-green-800 mb-2">
                  100% Refund
                </h3>
                <p className="text-green-700">
                  Cancel before puja date for full refund
                </p>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-red-800 mb-2">
                  No Refund
                </h3>
                <p className="text-red-700">
                  After puja is performed at temple
                </p>
              </div>
            </div>

            <div className="space-y-10">
              {/* Section 1 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    1
                  </span>
                  Refund Eligibility
                </h2>
                <div className="text-gray-600 space-y-4 ml-11">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                      <CheckCircle className="h-5 w-5 mr-2" />
                      Full Refund Scenarios (100% Amount)
                    </h4>
                    <ul className="list-disc list-inside space-y-2 text-green-700">
                      <li>
                        Cancellation request made{" "}
                        <strong>before the scheduled puja date</strong>
                      </li>
                      <li>
                        Cancellation at any time before pandit begins the
                        ceremony
                      </li>
                      <li>
                        Service cancellation due to temple unavailability or
                        emergencies
                      </li>
                      <li>
                        Technical issues preventing puja booking confirmation
                      </li>
                      <li>Duplicate payments or payment processing errors</li>
                    </ul>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-semibold text-red-800 mb-3 flex items-center">
                      <XCircle className="h-5 w-5 mr-2" />
                      No Refund Scenarios
                    </h4>
                    <ul className="list-disc list-inside space-y-2 text-red-700">
                      <li>
                        <strong>After puja ceremony has been completed</strong>{" "}
                        at the temple
                      </li>
                      <li>
                        After video recording of your personalized puja is
                        created
                      </li>
                      <li>
                        After blessed prasad has been dispatched for delivery
                      </li>
                      <li>Change of mind after puja performance</li>
                      <li>
                        Dissatisfaction with spiritual outcomes or results
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 2 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    2
                  </span>
                  Refund Process Timeline
                </h2>
                <div className="text-gray-600 space-y-4 ml-11">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                      <Clock className="h-5 w-5 mr-2" />
                      Simple 3-Step Process
                    </h4>

                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm mr-4 mt-0.5">
                          1
                        </div>
                        <div>
                          <strong>Submit Cancellation Request</strong>
                          <p className="text-sm text-blue-700">
                            Contact us via phone, email, or WhatsApp before puja
                            date
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm mr-4 mt-0.5">
                          2
                        </div>
                        <div>
                          <strong>Immediate Confirmation</strong>
                          <p className="text-sm text-blue-700">
                            We confirm cancellation and initiate refund within
                            24 hours
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm mr-4 mt-0.5">
                          3
                        </div>
                        <div>
                          <strong>Refund Credited</strong>
                          <p className="text-sm text-blue-700">
                            Full amount credited to original payment method
                            within 3 working days
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-yellow-800 mb-2">
                          Important Timeline Note
                        </h4>
                        <p className="text-yellow-700 text-sm">
                          Refund processing time may vary based on your bank or
                          payment provider. While we initiate refunds within 3
                          working days, it may take 5-7 business days to reflect
                          in your account depending on your financial
                          institution.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 3 */}
              {/* Section 3 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    3
                  </span>
                  Refund Methods
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    <strong>We process all refunds manually</strong> to ensure
                    accuracy and security:
                  </p>

                  <div className="space-y-3">
                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Google Pay:</strong> Refund directly to your
                        Google Pay account using your registered mobile number
                      </div>
                    </div>

                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Bank Transfer:</strong> Direct transfer to your
                        bank account using account details you provide
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                    <h4 className="font-semibold text-blue-800 mb-2">
                      Information We'll Request
                    </h4>
                    <p className="text-blue-700 text-sm mb-2">
                      Upon cancellation approval, we will contact you to
                      collect:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-blue-700 text-sm">
                      <li>
                        Preferred refund method (Google Pay or Bank Transfer)
                      </li>
                      <li>For Google Pay: Registered mobile number</li>
                      <li>
                        For Bank Transfer: Account holder name, account number,
                        IFSC code, bank name
                      </li>
                      <li>Verification of identity for security purposes</li>
                    </ul>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-green-800 mb-2">
                          Manual Processing Benefits
                        </h4>
                        <p className="text-green-700 text-sm">
                          Our manual refund process ensures personalized
                          attention, reduces errors, and allows us to assist you
                          with any specific requirements or concerns during the
                          refund process.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 4 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    4
                  </span>
                  Special Circumstances
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <div className="space-y-4">
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <h4 className="font-semibold text-purple-800 mb-2">
                        Temple Emergency or Force Majeure
                      </h4>
                      <p className="text-purple-700 text-sm">
                        In rare cases of temple closure due to natural
                        disasters, government restrictions, or other force
                        majeure events, full refunds will be processed
                        automatically within 5 working days.
                      </p>
                    </div>

                    <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                      <h4 className="font-semibold text-teal-800 mb-2">
                        Technical Issues
                      </h4>
                      <p className="text-teal-700 text-sm">
                        If technical problems prevent video recording or prasad
                        delivery, we will either re-perform the service or
                        provide full refund as per your preference.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 5 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    5
                  </span>
                  How to Request a Refund
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    To request a refund, contact us through any of the following
                    methods
                    <strong> before your scheduled puja date:</strong>
                  </p>

                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h4 className="font-semibold text-orange-800 mb-3">
                      Contact Methods
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Phone className="h-4 w-4 text-orange-500 mr-2" />
                        <span className="font-medium">Phone:</span>
                        <span className="ml-2">
                          +91 9699588995
                        </span>
                      </div>
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 text-orange-500 mr-2" />
                        <span className="font-medium">Email:</span>
                        <span className="ml-2">
                          ambabaimahalaxmipuja@gmail.com
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-green-500 mr-2">💬</span>
                        <span className="font-medium">WhatsApp:</span>
                        <span className="ml-2">
                          +91 9699588995 (Instant response)
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2">
                      Information to Provide
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-blue-700 text-sm">
                      <li>Your booking ID or reference number</li>
                      <li>Registered name and phone number</li>
                      <li>Service name and scheduled date</li>
                      <li>Reason for cancellation (optional)</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 6 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    6
                  </span>
                  Refund Confirmation
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    Once we process your refund request, you will receive
                    confirmation through multiple channels:
                  </p>

                  <div className="space-y-3">
                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Immediate SMS:</strong> Confirmation message
                        sent to your registered mobile number
                      </div>
                    </div>

                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>WhatsApp Notification:</strong> Detailed refund
                        confirmation with transaction reference
                      </div>
                    </div>

                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Email Receipt:</strong> Official refund receipt
                        sent to your registered email
                      </div>
                    </div>

                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Bank/Payment Notification:</strong> Your bank or
                        payment provider will send credit confirmation when
                        funds are received
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 7 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    7
                  </span>
                  Frequently Asked Questions
                </h2>
                <div className="text-gray-600 space-y-4 ml-11">
                  <div className="space-y-4">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-800 mb-2">
                        Q: Can I get a partial refund if I cancel on the puja
                        day?
                      </h4>
                      <p className="text-gray-600 text-sm">
                        <strong>A:</strong> Yes, you can get a full 100% refund
                        if you cancel before the puja ceremony begins, even on
                        the same day. However, once the pandit starts the
                        ritual, no refund is available.
                      </p>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-800 mb-2">
                        Q: What if I'm not satisfied with the puja video
                        quality?
                      </h4>
                      <p className="text-gray-600 text-sm">
                        <strong>A:</strong> We strive for quality videos. If
                        there are technical issues affecting video quality, we
                        will re-record the ceremony or offer appropriate
                        compensation, but spiritual satisfaction is not grounds
                        for refund.
                      </p>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-800 mb-2">
                        Q: Can I reschedule instead of canceling?
                      </h4>
                      <p className="text-gray-600 text-sm">
                        <strong>A:</strong> Yes! We encourage rescheduling over
                        cancellation. You can reschedule your puja to any future
                        date without any additional charges. Contact us to
                        reschedule.
                      </p>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-800 mb-2">
                        Q: Are there any cancellation fees?
                      </h4>
                      <p className="text-gray-600 text-sm">
                        <strong>A:</strong> No, there are absolutely no
                        cancellation fees or hidden charges. You get 100% of
                        your payment back when you cancel before the puja date.
                      </p>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-800 mb-2">
                        Q: What if prasad gets damaged during delivery?
                      </h4>
                      <p className="text-gray-600 text-sm">
                        <strong>A:</strong> We pack prasad with utmost care, but
                        if it arrives damaged due to courier issues, we will
                        send a replacement at no extra cost or provide
                        appropriate compensation.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 8 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    8
                  </span>
                  Dispute Resolution
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    If you have any concerns about refund processing or
                    disputes:
                  </p>

                  <div className="space-y-3">
                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Step 1:</strong> Contact our customer support
                        team directly for immediate resolution
                      </div>
                    </div>

                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Step 2:</strong> If unresolved, escalate to our
                        temple management within 7 days
                      </div>
                    </div>

                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div>
                        <strong>Step 3:</strong> Legal disputes will be handled
                        under Kolhapur jurisdiction as per our Terms &
                        Conditions
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 9 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm mr-3">
                    9
                  </span>
                  Contact Information
                </h2>
                <div className="text-gray-600 space-y-3 ml-11">
                  <p>
                    For any refund-related queries or assistance, reach out to
                    us:
                  </p>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mt-4">
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Phone className="h-4 w-4 text-orange-500 mr-2" />
                        <span className="font-medium">Support:</span>
                        <span className="ml-2">+91 9699588995</span>
                      </div>
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 text-orange-500 mr-2" />
                        <span className="font-medium">Email Support:</span>
                        <span className="ml-2">
                          ambabaimahalaxmipuja@gmail.com
                        </span>
                      </div>
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 text-orange-500 mr-2" />
                        <span className="font-medium">Temple Address:</span>
                        <span className="ml-2">
                          Shree Mahalaxmi Ambabai Temple, Kolhapur, Maharashtra
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm mt-4">
                    <strong>Average Response Time:</strong> Immediate for
                    phone/WhatsApp, within 2 hours for email queries.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-12 p-6 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-lg">
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold mb-2">Our Refund Guarantee</h4>
                  <p className="text-sm">
                    We stand behind our simple refund policy. Your spiritual
                    journey with us should be worry-free. If you have any doubts
                    about our refund process, please contact us before booking.
                    We're here to ensure your complete peace of mind.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default RefundPolicy;
